import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useLayoutEffect,
  useSyncExternalStore,
} from 'react';
import { InteractionStatus } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';

import { ApiSession } from './apiSession';

const ApiAuthContext = createContext<ApiSession | null>(null);

export function ApiAuthProvider({
  session,
  children,
}: {
  session: ApiSession;
  children: ReactNode;
}) {
  const { instance, accounts, inProgress } = useMsal();
  const account = instance.getActiveAccount() ?? accounts[0] ?? null;

  useLayoutEffect(() => {
    session.synchronize(account, inProgress);
  }, [session, account, inProgress]);

  useEffect(() => {
    const refresh = () => {
      if (
        document.visibilityState === 'hidden' ||
        !account ||
        inProgress !== InteractionStatus.None ||
        session.getSnapshot().error?.requiresInteraction
      ) {
        return;
      }
      void session.getToken().catch(() => {
        /* The shared recovery UI displays the error. */
      });
    };
    globalThis.addEventListener('focus', refresh);
    globalThis.addEventListener('online', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      globalThis.removeEventListener('focus', refresh);
      globalThis.removeEventListener('online', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [session, account, inProgress]);

  return (
    <ApiAuthContext.Provider value={session}>
      {children}
    </ApiAuthContext.Provider>
  );
}

export function useApiAuth() {
  const session = useContext(ApiAuthContext);
  if (!session) {
    throw new Error('API authentication requires ApiAuthProvider.');
  }
  const state = useSyncExternalStore(session.subscribe, session.getSnapshot);
  return { session, ...state };
}
